import { IConfigSeller } from '@/models/configSeller'
import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { Button, Card, CardBody, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { CSSProperties, FC, useEffect, useState } from 'react'
import { MdCheckCircle, MdLaptop, MdOutlineInfo, MdOutlineTabletMac, MdOutlineVideogameAsset, MdOutlineWatch, MdSmartphone } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

interface IOwnProps {
  isOpen: boolean
  onClose: () => void
  sbuList: any[]
}

const CAPACITY_LIST = ['32 GB', '64 GB', '128 GB', '256 GB', '512 GB', '1 TB', '2 TB']
const OTHER_BRAND_KEY = 'otra'

const getSbuName = (idSbu: number) => {
  switch (idSbu) {
    case 1:
      return 'Smartphone'
    case 2:
      return 'Notebook'
    case 3:
      return 'Tablet'
    case 5:
      return 'Smartwatch'
    case 12:
      return import.meta.env.VITE_COUNTRY === 'PE' ? 'Consola' : 'Consolas'
    default:
      return 'N/D'
  }
}

const getIcon = (idSbu: number) => {
  switch (idSbu) {
    case 1:
      return <MdSmartphone className='mx-auto text-2xl' />
    case 2:
      return <MdLaptop className='mx-auto text-2xl' />
    case 3:
      return <MdOutlineTabletMac className='mx-auto text-2xl' />
    case 5:
      return <MdOutlineWatch className='mx-auto text-2xl' />
    case 12:
      return <MdOutlineVideogameAsset className='mx-auto text-2xl' />
    default:
      return null
  }
}

const ModalDeviceNotFound: FC<IOwnProps> = ({ isOpen, onClose, sbuList }) => {
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const userState = useSelector((store: { user: IUser }) => store.user)
  const { slug } = useParams<{ slug: string }>()

  // El formulario también lo usan ejecutivos logueados en tienda: en ese caso
  // dejamos registrado quién y desde qué tienda solicitó el dispositivo.
  const isLogged = userState.id !== 0

  const [data, setData] = useState<{ loading: boolean, brandLoading: boolean, sent: boolean, brandList: any[], idSbu: number, idBrand: string, customBrand: string, model: string, capacity: string }>({
    loading: false,
    brandLoading: false,
    sent: false,
    brandList: [],
    idSbu: 0,
    idBrand: '',
    customBrand: '',
    model: '',
    capacity: ''
  })

  const loadBrands = async (idSbu: number) => {
    setData(prev => ({ ...prev, idSbu, brandLoading: true, brandList: [], idBrand: '', customBrand: '' }))
    try {
      const res = await webApiService.getAllBrandBySbu(String(idSbu), String(slug))
      setData(prev => ({ ...prev, brandList: res.success ? res.data : [], brandLoading: false }))
    } catch (error: any) {
      console.log(error)
      enqueueSnackbar(error.message, { variant: 'error' })
      setData(prev => ({ ...prev, brandLoading: false }))
    }
  }

  // Al abrir el modal se parte desde cero con la primera categoría disponible.
  useEffect(() => {
    if (!isOpen) return
    setData({ loading: false, brandLoading: false, sent: false, brandList: [], idSbu: 0, idBrand: '', customBrand: '', model: '', capacity: '' })
    if (sbuList.length > 0) loadBrands(sbuList[0].id_sbu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const isOtherBrand = data.idBrand === OTHER_BRAND_KEY
  // El smartwatch no maneja capacidad en el catálogo, tampoco la pedimos aquí.
  const showCapacity = data.idSbu !== 5

  const getBrandName = () => {
    if (isOtherBrand) return data.customBrand.trim()
    const brand = data.brandList.find((item: any) => String(item.id_brand) === data.idBrand)
    return brand ? brand.brand : ''
  }

  const isFormValid = data.idSbu !== 0 &&
    getBrandName().length > 0 &&
    data.model.trim().length > 1 &&
    (!showCapacity || data.capacity !== '')

  const submit = async () => {
    if (!isFormValid) return
    setData(prev => ({ ...prev, loading: true }))
    try {
      await webApiService.saveDeviceRequest({
        seller: String(slug),
        id_seller: configSeller.config.idSeller,
        id_sbu: data.idSbu,
        sbu: getSbuName(data.idSbu),
        id_brand: isOtherBrand ? null : Number(data.idBrand),
        brand: getBrandName(),
        model: data.model.trim(),
        capacity: showCapacity ? data.capacity : null,
        id_user: isLogged ? userState.id : null,
        id_store: isLogged && userState.id_store !== 0 ? userState.id_store : null
      })
      setData(prev => ({ ...prev, loading: false, sent: true }))
    } catch (error: any) {
      console.log(error)
      enqueueSnackbar('No pudimos registrar tu dispositivo, intenta nuevamente.', { variant: 'error' })
      setData(prev => ({ ...prev, loading: false }))
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement='center'
      backdrop='blur'
      size='2xl'
      scrollBehavior='inside'
      // Los listbox de Select se renderizan en un portal fuera del modal, por lo que
      // NextUI los detecta como click "afuera" y cerraba el modal al elegir una opción.
      isDismissable={false}
    >
      <ModalContent style={{ '--seller-color': `${configSeller.config.color}` } as CSSProperties}>
        {(closeModal) => (
          data.sent
            ? (
              <>
                <ModalBody className='text-center py-10'>
                  <MdCheckCircle className='mx-auto text-5xl text-green-500' />
                  <h3 className='text-xl font-bold'>¡Solicitud enviada con éxito!</h3>
                  <p className='text-sm text-gray-600'>
                    Registramos tu dispositivo. Nuestro equipo lo revisará y evaluará su incorporación al catálogo de Trade-in próximamente.
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button color='primary' onPress={closeModal}>Cerrar</Button>
                </ModalFooter>
              </>
              )
            : (
              <>
                <ModalHeader className='flex flex-col gap-1'>Registra tu dispositivo</ModalHeader>
                <ModalBody>
                  <div className='flex items-start gap-2 rounded-xl border-1 border-gray-200 bg-gray-50 p-3 text-sm text-gray-600'>
                    <MdOutlineInfo className='shrink-0 text-xl' />
                    <span>¿No encontraste tu dispositivo en el catálogo? Ingrésalo aquí y lo evaluaremos para incorporarlo próximamente.</span>
                  </div>

                  <div>
                    <p className='mb-2'>Seleccione una categoría:</p>
                    <div className='grid grid-cols-3 gap-3 md:grid-cols-5'>
                      {sbuList.map((item: any) => (
                        <Card
                          key={item.id_sbu}
                          isPressable
                          onPress={() => loadBrands(item.id_sbu)}
                          shadow='none'
                          className={data.idSbu === item.id_sbu
                            ? 'p-1 text-center border-1 outline-2 outline-offset-0 outline-[var(--seller-color)] rounded-md border-[var(--seller-color)] shadow-md'
                            : 'p-1 text-center border-1 rounded-md border-gray-300 shadow-none hover:border-[var(--seller-color)] hover:bg-gray-100'}
                        >
                          <CardBody className='px-1 py-2 text-center'>
                            {getIcon(item.id_sbu)}
                            <p className='mt-1 text-tiny font-bold'>{getSbuName(item.id_sbu)}</p>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className='mb-2'>Seleccione una marca:</p>
                    <Select
                      variant='bordered'
                      size='lg'
                      aria-label='Seleccione una marca'
                      placeholder='Seleccionar marca'
                      className='w-full'
                      isLoading={data.brandLoading}
                      isDisabled={data.brandLoading || data.idSbu === 0}
                      selectedKeys={data.idBrand === '' ? [] : [data.idBrand]}
                      onChange={(e) => {
                        if (e.target.value === '') return
                        setData(prev => ({ ...prev, idBrand: e.target.value, customBrand: '' }))
                      }}
                    >
                      {[
                        ...data.brandList.map((brand: any) => (
                          <SelectItem key={String(brand.id_brand)} textValue={brand.brand}>
                            {brand.brand}
                          </SelectItem>
                        )),
                        <SelectItem key={OTHER_BRAND_KEY} textValue='Otra'>
                          Otra
                        </SelectItem>
                      ]}
                    </Select>
                    {isOtherBrand
                      ? (
                        <Input
                          variant='bordered'
                          size='lg'
                          className='mt-2'
                          aria-label='Ingrese la marca'
                          placeholder='Escribe el nombre de la marca'
                          value={data.customBrand}
                          onChange={(e) => setData(prev => ({ ...prev, customBrand: e.target.value }))}
                        />
                        )
                      : null}
                  </div>

                  <div>
                    <p className='mb-2'>Ingrese el modelo:</p>
                    <Input
                      variant='bordered'
                      size='lg'
                      aria-label='Ingrese el modelo'
                      placeholder='Ej: iPhone 15 Pro Max'
                      value={data.model}
                      onChange={(e) => setData(prev => ({ ...prev, model: e.target.value }))}
                    />
                  </div>

                  <div hidden={!showCapacity}>
                    <p className='mb-2'>Seleccione {data.idSbu === 2 ? 'almacenamiento' : 'memoria'}:</p>
                    <Select
                      variant='bordered'
                      size='lg'
                      aria-label='Seleccione capacidad'
                      placeholder='Seleccionar capacidad'
                      className='w-full'
                      selectedKeys={data.capacity === '' ? [] : [data.capacity]}
                      onChange={(e) => {
                        if (e.target.value === '') return
                        setData(prev => ({ ...prev, capacity: e.target.value }))
                      }}
                    >
                      {CAPACITY_LIST.map((capacity) => (
                        <SelectItem key={capacity} textValue={capacity}>
                          {capacity}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button color='danger' variant='flat' onPress={closeModal}>
                    Cancelar
                  </Button>
                  <Button
                    color='primary'
                    onPress={submit}
                    isLoading={data.loading}
                    isDisabled={!isFormValid}
                  >
                    Enviar solicitud
                  </Button>
                </ModalFooter>
              </>
              )
        )}
      </ModalContent>
    </Modal>
  )
}

export default ModalDeviceNotFound
